import { Component, OnInit, OnDestroy, ElementRef, ViewChild, AfterViewInit } from '@angular/core';
import jsQR, { QRCode } from 'jsqr';
import { Parameters } from '@app/components/parameters/parameters';
import { ParametersService } from '@app/components/parameters/parameters.service';
import { Router } from '@angular/router';
import { MachineInfosService } from '@app/components/machine-infos/machine-infos.service';
import { PoeModalService, TranslationService } from '@app/services';
import { Point } from 'jsqr/dist/locator';


@Component({
    selector: 'app-qrscanner',
    templateUrl: './qrscanner.component.html',
    styleUrls: ['./qrscanner.component.css'],
    standalone: false
})
export class QrscannerComponent implements OnInit, OnDestroy, AfterViewInit {

    @ViewChild('scanCanvas') scanCanvas: ElementRef<HTMLCanvasElement>;
    canvasContext: CanvasRenderingContext2D;
    video: HTMLVideoElement;
    cameraStream: MediaStream;
    params: Parameters;
    hasCode: boolean = false;
    machineKey: string;

    constructor(
        private readonly parametersService: ParametersService,
        private readonly router: Router,
        private readonly machininfosService: MachineInfosService,
        private readonly modalService: PoeModalService,
        private readonly translate: TranslationService
    ) {
    }
    ngAfterViewInit(): void {
        this.canvasContext = this.scanCanvas.nativeElement.getContext('2d');
    }

    async ngOnInit(): Promise<void> {
        this.parametersService.getParametersObservable().subscribe(par => this.params = par);
        // ---------- HEADS UP! ----------
        // If you are debugging with Chrome it might happen that there is a bug IN CHROME,
        // which causes the camera to continue recording after the stream is stopped
        this.video = document.createElement('video');

        try {
            if (navigator?.mediaDevices) {
                this.cameraStream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
                if (this.cameraStream.getVideoTracks().length === 0) {
                    this.modalService.showInfoModal({
                        title: this.translate.translateInstant('Error'),
                        message: this.translate.translateInstant('NoCamera'),
                        confirmText: this.translate.translateInstant('OK')
                    }).subscribe(_ => {
                        this.router.navigate(['/machinesearch']);
                    });
                }
                this.video.srcObject = this.cameraStream;
                this.video.setAttribute('playsinline', 'true'); // required to tell iOS safari we don't want fullscreen
                await this.video.play();
                requestAnimationFrame(() => this.tick());
            } else {
                this.modalService.showInfoModal({
                    title: this.translate.translateInstant('Error'),
                    message: this.translate.translateInstant('NoCamera'),
                    confirmText: this.translate.translateInstant('OK')
                }).subscribe(_ => {
                    this.router.navigate(['/machinesearch']);
                });
            }
        } catch {
            this.modalService.showInfoModal({
                title: this.translate.translateInstant('Error'),
                message: this.translate.translateInstant('NoCamera'),
                confirmText: this.translate.translateInstant('OK')
            }).subscribe(_ => {
                this.router.navigate(['/machinesearch']);
            });
        }
    }

    drawLine(begin: Point, end: Point, color: string): void {
        this.canvasContext.beginPath();
        this.canvasContext.moveTo(begin.x, begin.y);
        this.canvasContext.lineTo(end.x, end.y);
        this.canvasContext.lineWidth = 4;
        this.canvasContext.strokeStyle = color;
        this.canvasContext.stroke();
    }

    tick(): void {
        if (this.video.readyState === this.video.HAVE_ENOUGH_DATA && this.hasCode === false) {
            this.scanCanvas.nativeElement.hidden = false;

            this.scanCanvas.nativeElement.height = this.video.videoHeight;
            this.scanCanvas.nativeElement.width = this.video.videoWidth;
            this.canvasContext.drawImage(this.video, 0, 0, this.scanCanvas.nativeElement.width, this.scanCanvas.nativeElement.height);
            const imageData: ImageData = this.canvasContext.getImageData(0, 0, this.scanCanvas.nativeElement.width, this.scanCanvas.nativeElement.height);
            const code: QRCode = jsQR(imageData.data, imageData.width, imageData.height);
            if (code?.data) {
                this.drawLine(code.location.topLeftCorner, code.location.topRightCorner, '#FF3B58');
                this.drawLine(code.location.topRightCorner, code.location.bottomRightCorner, '#FF3B58');
                this.drawLine(code.location.bottomRightCorner, code.location.bottomLeftCorner, '#FF3B58');
                this.drawLine(code.location.bottomLeftCorner, code.location.topLeftCorner, '#FF3B58');
                const qrcodeDetected = code.data;

                const machineCode = qrcodeDetected.slice(qrcodeDetected.lastIndexOf('/') + 1);

                if (machineCode) {
                    this.hasCode = true;
                    this.machineKey = machineCode;
                    this.params.fahrzeugNr = this.machineKey;

                    this.modalService.showConfirmationModal({
                        title: this.translate.translateInstant('ThisMachine'),
                        message: this.machineKey,
                        confirmText: this.translate.translateInstant('UseSugg'),
                        cancelText: this.translate.translateInstant('Cancel')
                    }).subscribe({
                        next: confirmed => {
                            if (confirmed) {
                                this.onClickJa();
                            } else {
                                this.onClickNein();
                            }
                        },
                        error: _error => {
                            this.onClickNein();
                        }
                    });
                }
            }
        }
        requestAnimationFrame(() => this.tick());
    }

    onClickJa(): void {
        this.params.fahrzeugNr = this.params.fahrzeugNr.replace('VBP', '').replace(/^0+/, '');

        this.machininfosService.fetchMachineData(this.params.fahrzeugNr, 'de_AT')
            .subscribe({
                next: infos => {
                    if (infos.maktx) {
                        this.cameraStream.getTracks().forEach(track => track.stop());
                        this.cameraStream = null;

                        if (this.params.fahrzeugNr && this.params.fahrzeugNr !== '') {
                            this.router.navigate(['/machine', this.params.fahrzeugNr]);
                        }
                    } else {
                        this.modalService.showInfoModal({
                            title: 'Invalid Input',
                            message: 'Right now this QR-Code is not supported, please try again in some days',
                            confirmText: this.translate.translateInstant('Confirm')
                        }).subscribe(_ => {
                            this.router.navigate(['/machinesearch']);
                        });
                    }
                },
                error: () => {
                    this.modalService.showInfoModal({
                        title: 'Invalid Input',
                        message: 'Right now this QR-Code is not supported, please try again in some days',
                        confirmText: this.translate.translateInstant('Confirm')
                    }).subscribe(_ => {
                        this.router.navigate(['/machinesearch']);
                    });
                }
            });
    }

    ngOnDestroy() {
        if (this.cameraStream) {
            this.cameraStream.getTracks().forEach(track => {
                track.stop();
            });
            this.cameraStream = null;
        }
    }

    onClickNein(): void {
        this.hasCode = false;
    }

    onClickOK() {
        this.router.navigate(['/machinesearch']);
    }
}
